import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Lock, Map as MapIcon, Table as TableIcon, LogOut, AlertCircle, ArrowRight, Users } from "lucide-react";

/** Simple session-based admin gate. Not cryptographic — matches the existing trivial `?admin=1` model.
 *  Password can be overridden via `VITE_ADMIN_PASSWORD` env var (defaults to "admin"). */
const ADMIN_PASSWORD = (import.meta.env.VITE_ADMIN_PASSWORD as string | undefined) ?? "admin";
const SESSION_KEY = "ie_admin_authed";

export function isAdminAuthed(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearAdminAuth() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* noop */
  }
}

export default function AdminLandingPage() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState<boolean>(isAdminAuthed());
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Admin — Immense Estate";
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
      setAuthed(true);
      setError(null);
    } else {
      setError("Incorrect password");
    }
  };

  const logout = () => {
    clearAdminAuth();
    setAuthed(false);
    setPassword("");
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-navy-900 text-white">
      {/* Ambient glows */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-sky-500/10 blur-[120px]" />
        <div className="absolute -right-56 top-24 h-[680px] w-[680px] rounded-full bg-amber-500/10 blur-[120px]" />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 py-5 md:px-10">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-3 transition hover:opacity-80"
        >
          <div className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm">
            <Shield className="h-5 w-5 text-amber-400" />
          </div>
          <div className="leading-tight text-left">
            <div className="text-[10px] font-semibold tracking-[0.3em] text-zinc-500 uppercase">Immense Estate</div>
            <div className="text-sm font-semibold text-white">Administration</div>
          </div>
        </button>
        {authed && (
          <button
            onClick={logout}
            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-white/80 transition hover:bg-white/10"
          >
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        )}
      </header>

      <main className="relative z-10 mx-auto flex w-full max-w-5xl items-center justify-center px-6 pb-20">
        {!authed ? (
          /* ── Login ── */
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-12 w-full max-w-md rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8 shadow-2xl backdrop-blur-xl"
          >
            <div className="mb-6 flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-navy-950">
                <Lock className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[10px] font-bold tracking-[0.25em] text-amber-400 uppercase">Sign In</div>
                <div className="text-lg font-bold text-white">Administrator Access</div>
              </div>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <label className="block">
                <span className="block text-[11px] font-semibold tracking-wider text-white/60 uppercase mb-1.5">Password</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  placeholder="Enter admin password"
                  className="w-full rounded-lg border border-white/10 bg-navy-950/70 px-3.5 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/40"
                />
              </label>

              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
                  <AlertCircle className="h-3.5 w-3.5" /> {error}
                </div>
              )}

              <button
                type="submit"
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-3 text-sm font-bold tracking-wide text-navy-950 shadow-lg shadow-amber-500/20 transition hover:brightness-110"
              >
                Sign In <ArrowRight className="h-4 w-4" />
              </button>
            </form>

            <button
              onClick={() => navigate("/")}
              className="mt-4 w-full text-center text-[11px] text-white/40 hover:text-white/70 transition"
            >
              ← Back to site
            </button>
          </motion.div>
        ) : (
          /* ── Admin chooser ── */
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-8 w-full"
          >
            <div className="mb-8 text-center">
              <div className="text-[11px] font-bold tracking-[0.3em] text-amber-400 uppercase">Welcome, Administrator</div>
              <h1 className="mt-2 text-3xl font-bold tracking-tight md:text-4xl">Choose your admin mode</h1>
              <p className="mt-2 text-sm text-white/60">Both modes share the same backend — changes save to the same database.</p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <AdminTile
                onClick={() => navigate("/masterplan?admin=1")}
                icon={<MapIcon className="h-6 w-6" />}
                tag="Interactive"
                title="Map Admin"
                desc="Drag plots, position labels, manage floors, blueprints, POIs and villa types on the visual masterplan."
                accent="sky"
              />
              <AdminTile
                onClick={() => navigate("/table-admin?admin=1")}
                icon={<TableIcon className="h-6 w-6" />}
                tag="Non-interactive"
                title="Table Admin"
                desc="Spreadsheet-style bulk editing with CSV import/export and full database backup downloads."
                accent="amber"
              />
              <AdminTile
                onClick={() => navigate("/admin/clients")}
                icon={<Users className="h-6 w-6" />}
                tag="Access Control"
                title="Client Access"
                desc="Create client credentials, copy them for email, and lock or remove access at any time."
                accent="emerald"
              />
            </div>

            <div className="mt-10 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 text-[12px] text-white/55">
              <b className="text-white/80">Tip:</b> Positions and visual data are preserved when you switch between modes — the Table Admin will never overwrite map-positioning set in Map Admin.
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}

function AdminTile({
  onClick,
  icon,
  tag,
  title,
  desc,
  accent,
  active = false,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  tag: string;
  title: string;
  desc: string;
  accent: "sky" | "amber" | "emerald";
  active?: boolean;
}) {
  const accentMap = {
    sky: {
      border: "hover:border-sky-400/40",
      activeBorder: "border-sky-400/40",
      glow: "from-sky-500/10 to-transparent",
      iconBg: "from-sky-400 to-sky-600",
      tagText: "text-sky-300",
    },
    amber: {
      border: "hover:border-amber-400/40",
      activeBorder: "border-amber-400/40",
      glow: "from-amber-500/10 to-transparent",
      iconBg: "from-amber-400 to-amber-600",
      tagText: "text-amber-300",
    },
    emerald: {
      border: "hover:border-emerald-400/40",
      activeBorder: "border-emerald-400/40",
      glow: "from-emerald-500/10 to-transparent",
      iconBg: "from-emerald-400 to-emerald-600",
      tagText: "text-emerald-300",
    },
  }[accent];

  return (
    <button
      onClick={onClick}
      className={`group relative overflow-hidden rounded-2xl border bg-white/[0.03] p-6 text-left shadow-2xl backdrop-blur-xl transition ${active ? accentMap.activeBorder + " border-opacity-100" : "border-white/[0.06] " + accentMap.border}`}
    >
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${accentMap.glow} transition ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className={`grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br ${accentMap.iconBg} text-navy-950`}>{icon}</div>
          <span className={`text-[10px] font-bold tracking-[0.2em] uppercase ${accentMap.tagText}`}>{tag}</span>
        </div>
        <div className="mt-5 text-xl font-bold text-white">{title}</div>
        <p className="mt-1.5 text-[13px] leading-6 text-white/55">{desc}</p>
        <div className="mt-5 flex items-center gap-1.5 text-[12px] font-semibold text-white/70 transition group-hover:text-white">
          Enter <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-1" />
        </div>
      </div>
    </button>
  );
}
